/**
 * Scorecard Visibility Helpers
 * Utility functions for scorecard visibility feature
 */

import dayjs from 'dayjs';
import {
  type ScorecardItem,
  type ScorecardVisibilityMode,
  type SortOption,
  type SortConfig,
} from './types';
import relativeTime from 'dayjs/plugin/relativeTime';
import { type InterviewType } from '@/types/interview';
dayjs.extend(relativeTime);

/**
 * Check if current user has submitted their scorecard
 */
export const hasUserSubmittedScorecard = (
  scorecards: ScorecardItem[],
  currentUserId: number
): boolean => {
  return scorecards.some(
    (sc) =>
      sc.is_current &&
      sc.reviewer.id === currentUserId &&
      sc.status === 'submitted'
  );
};

/**
 * Get current user's scorecard from the list
 */
export const getCurrentUserScorecard = (
  scorecards: ScorecardItem[],
  currentUserId: number
): ScorecardItem | undefined => {
  return scorecards.find(
    (sc) => sc.is_current && sc.reviewer.id === currentUserId
  );
};

/**
 * Whether the current user may always see all scorecards for an interview —
 * super-admins, users whose org_role grants submit_scorecards_on_behalf, or
 * any member of the job's hiring team. These viewers bypass the visibility
 * setting and the "score first" bias gate.
 *
 * @param hasCurrentUserScorecard - pass true when the caller knows the user
 *   has their own scorecard in this interview. Used for the fallback path when
 *   `ats_job.roles` is absent from the API response.
 */
export const isPrivilegedScorecardViewer = (
  interview: InterviewType,
  currUser?: {
    id?: number;
    is_super_admin?: boolean;
    org_role?: { name?: string };
  },
  hasCurrentUserScorecard?: boolean
): boolean => {
  if (!currUser?.id) return false;
  if (currUser.is_super_admin) return true;
  if (currUser.org_role?.name === 'Super Admin') return true;
  // Preferred: explicit hiring-team membership from the job's role list.
  if (
    interview?.ats_job?.roles?.some((role) =>
      role.users?.some((user) => user.id === currUser.id)
    )
  )
    return true;
  // Fallback when the interview API doesn't include ats_job.roles: a user
  // with edit access but no own interviewer scorecard is a non-interviewing
  // hiring-team member (recruiter / HM) — treat as privileged.
  if (interview?.can_edit && !hasCurrentUserScorecard) return true;
  return false;
};

/**
 * Determine if table view should be shown
 */
export const shouldShowTableView = (
  mode: ScorecardVisibilityMode,
  _: string
): boolean => {
  // if (tabId === 'scorecard') return false;
  return mode === 'after_submission' || mode === 'always';
};

/**
 * Determine if history section should be shown
 */
export const shouldShowHistorySection = (
  mode: ScorecardVisibilityMode,
  hasCurrentUserSubmitted: boolean,
  tabId: string,
  canEdit?: boolean
): boolean => {
  if (tabId === 'scorecard') return false;
  if (mode === 'never' && canEdit) return true;
  if (mode === 'always') return true;
  if (mode === 'after_submission') return hasCurrentUserSubmitted || canEdit;
  return false;
};

/**
 * Determine if user can view another user's scorecard
 */
export const canViewOtherScorecard = (
  scorecard: ScorecardItem,
  currentUserId: number,
  mode: ScorecardVisibilityMode,
  hasCurrentUserSubmitted: boolean,
  interview: InterviewType
): boolean => {
  // Always can view own scorecard
  if (scorecard.reviewer.id === currentUserId) return true;

  // Additional frontend checks based on mode
  if (mode === 'never') {
    // If the user can edit the scorecard, they can view it
    if (scorecard.can_edit) {
      return true;
    }

    return false;
  }
  if (mode === 'always') return true;
  if (mode === 'after_submission')
    return hasCurrentUserSubmitted || interview?.can_edit;

  return false;
};

export const filterCurrentInterviewScorecards = (
  scorecards: ScorecardItem[],
  currentUserId: number,
  tabId: string,
  canEditInterview?: boolean
  // visibilityMode?: ScorecardVisibilityMode
): ScorecardItem[] => {
  const currentUserScorecard = getCurrentUserScorecard(
    scorecards,
    currentUserId
  );

  if (tabId === 'all_scorecards') {
    let filteredScorecards: ScorecardItem[] = scorecards;
    // By default, we filter out the current users's scorecard for all_scorecards tab in always mode
    filteredScorecards = filteredScorecards.filter(
      (sc) => sc.reviewer.id !== currentUserId
    );

    // For HM/Admin, if they dont have a scorecard, then we will show current interview scorecards
    // in the scorecard tab. So, we need to remove them from all_scorecards tab.
    if (!currentUserScorecard && canEditInterview) {
      filteredScorecards = filteredScorecards.filter((sc) => !sc.is_current);
    }

    return filteredScorecards;
  }

  // Filter out current user's scorecard only on all_scorecards tab
  if (currentUserScorecard && tabId === 'all_scorecards') {
    return scorecards.filter((sc) => sc.reviewer.id !== currentUserId);
  } else if (tabId === 'all_scorecards' && !canEditInterview) {
    // Only filter out current interview scorecards if user doesn't have edit permission
    return scorecards.filter((sc) => !sc.is_current);
  }

  return scorecards;
};

/**
 * Check if scorecard belongs to current user
 */
export const isOwnScorecard = (
  scorecard: ScorecardItem,
  currentUserId: number
): boolean => {
  return scorecard.reviewer.id === currentUserId;
};

/**
 * Format submission time relative to created time
 */
export const formatSubmissionTime = (
  submittedAt: string | undefined,
  createdAt?: string
): string => {
  if (!submittedAt) return '-';

  const submitted = dayjs(submittedAt);
  if (!submitted.isValid()) return '-';

  // If we have created_at, calculate relative time
  if (createdAt) {
    const created = dayjs(createdAt);
    if (created.isValid()) {
      const dayDiff = submitted.diff(created, 'day');
      if (dayDiff === 0) return 'Within a day';
      return `After ${dayDiff} day${dayDiff !== 1 ? 's' : ''}`;
    }
  }

  // Fallback to relative time from now
  return submitted.fromNow();
};

/**
 * Sort options configuration
 */
export const SORT_OPTIONS: SortConfig[] = [
  { label: 'Most recent', value: 'most_recent' },
  { label: 'Stage', value: 'stage' },
  { label: 'Rating (highest first)', value: 'rating_highest' },
  { label: 'Rating (lowest first)', value: 'rating_lowest' },
];

/**
 * Sort scorecards based on selected option
 */
export const sortScorecards = (
  scorecards: ScorecardItem[],
  sortBy: SortOption
): ScorecardItem[] => {
  const ratingOrder = ['strong_yes', 'yes', 'no', 'strong_no'];

  return [...scorecards].sort((a, b) => {
    switch (sortBy) {
      case 'most_recent': {
        const aTime = a.submitted_at ? dayjs(a.submitted_at).unix() : 0;
        const bTime = b.submitted_at ? dayjs(b.submitted_at).unix() : 0;
        return bTime - aTime;
      }
      case 'stage':
        return (a.stage?.id || 0) - (b.stage?.id || 0);

      case 'rating_highest': {
        const aRatingHigh = a.rating
          ? ratingOrder.indexOf(a.rating)
          : ratingOrder.length;
        const bRatingHigh = b.rating
          ? ratingOrder.indexOf(b.rating)
          : ratingOrder.length;
        return aRatingHigh - bRatingHigh;
      }
      case 'rating_lowest': {
        const aRatingLow = a.rating ? ratingOrder.indexOf(a.rating) : -1;
        const bRatingLow = b.rating ? ratingOrder.indexOf(b.rating) : -1;
        return bRatingLow - aRatingLow;
      }
      default:
        return 0;
    }
  });
};

/**
 * Get record type icon name
 */
export const getRecordTypeIcon = (
  recordType: string
): 'interview' | 'review' | 'assessment' => {
  const typeMap: Record<string, 'interview' | 'review' | 'assessment'> = {
    AtsInterview: 'interview',
    AtsReview: 'review',
    AtsAssessment: 'assessment',
  };
  return typeMap[recordType] || 'interview';
};

/**
 * Get record type label
 */
export const getRecordTypeLabel = (recordType: string): string => {
  const labelMap: Record<string, string> = {
    AtsInterview: 'Interview',
    AtsReview: 'Review',
    AtsAssessment: 'Assessment',
  };
  return labelMap[recordType] || 'Interview';
};
